import React, { useState, useEffect } from 'react';
import { Box, Paper } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBalanceSheetPartyWise, downloadBalanceSheetPartyWise } from '../../store/reportStore/reportThunk';
import BalanceSheetPartyWise from '../../components/Reports/BalanceSheetPartyWise';
import ReusableReportHeader from '../../components/Reports/ReusableReportHeader';
import Filters from '../../components/Filters/Filters';
import { ApiStatus } from '../../utils/api.types';
import { VocuherCategory } from '../../utils/common-utils';
import { toast } from 'react-toastify';

const BalanceSheetPartyWisePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { balanceSheetPartyWiseData, status, errorMessage } = useAppSelector((state) => state.reports);
  const { selectedAccount } = useAppSelector((state) => state.auth);
  const accountId = selectedAccount?._id;

  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  // Fetch report when filters change
  useEffect(() => {
    if (accountId && filters) {
      setIsLoading(true);
      // Add includePartyWise=true to ensure party breakdown is included
      const partyWiseFilters = `${filters}&includePartyWise=true`;
      dispatch(fetchBalanceSheetPartyWise({
        accountId,
        startDate: '',
        endDate: '',
        params: partyWiseFilters
      })).finally(() => {
        setIsLoading(false);
      });
    }
  }, [accountId, filters]);

  const handleDownloadPdf = () => {
    if (!accountId || !filters) {
      toast.error("Please select date range to download PDF");
      return false;
    }

    const partyWiseFilters = `${filters}&includePartyWise=true&isDownloadPdf=true`;
    return dispatch(downloadBalanceSheetPartyWise({
      accountId,
      startDate: '',
      endDate: '',
      params: partyWiseFilters
    })).unwrap().then((data) => {
      if (data) {
        // Create a Blob from the response data
        const blob = new Blob([data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        // Create a link element, set the href, and trigger the download
        const a = document.createElement("a");
        a.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        a.download = `Balance_Sheet_Party_Wise_${timestamp}.pdf`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  };

  const handleDownloadExcel = () => {
    if (!accountId || !filters) {
      toast.error("Please select date range to download Excel");
      return false;
    }

    const partyWiseFilters = `${filters}&includePartyWise=true&isDownloadExcel=true`;
    return dispatch(downloadBalanceSheetPartyWise({
      accountId,
      startDate: '',
      endDate: '',
      params: partyWiseFilters
    })).unwrap().then((data) => {
      if (data) {
        // Create a Blob from the response data
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = window.URL.createObjectURL(blob);

        // Create a link element, set the href, and trigger the download
        const a = document.createElement("a");
        a.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        a.download = `Balance_Sheet_Party_Wise_${timestamp}.xlsx`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  };

  return (
    <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2 }} className={'c-card2'}>
      {/* Scrollable content area */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        {/* Report Header */}
        <ReusableReportHeader
          reportType="BALANCE_SHEET_PARTY_WISE"
          title="Balance Sheet - Party Wise"
          subtitle={filterData?.periodString || ''}
          isLoading={isLoading || status === ApiStatus.LOADING}
          onDownloadPDF={handleDownloadPdf}
          onDownloadExcel={handleDownloadExcel}
          showPrint={false}
          showShare={false}
        />

        {/* Filters - No Paper wrapper */}
        <Box px={2} py={1.5}>
          <Filters
            type={VocuherCategory.SALE}
            filterHandler={filterOnChangeHandler}
            selectedFilters={filterdOnChangeHandler}
            hideFilter={true}
            hidePartySearch={true}
          />
        </Box>

        {/* Balance Sheet with Party Breakdown */}
        <Box px={2}>
          <BalanceSheetPartyWise
            data={balanceSheetPartyWiseData}
            loading={status === ApiStatus.LOADING || isLoading}
            error={errorMessage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BalanceSheetPartyWisePage;
